import { Invitation, Inviter, Registerer, RegistererState, Session, SessionState, URI, UserAgent, type UserAgentDelegate } from "sip.js";
import moment from "moment";

// Actions
import { doGetRamalRequest, doLeaveRamalRequest } from "src/store/call/actions";

// Types
import type { ReturnData } from "src/types";

// Models
import { DialerPSTN } from "src/models/pstn-call";
import { playDTMFTone } from "src/helpers";

// Logger
import { createSipLogConnector, logError, logEvent, logInfo, logMeasurement, logWarn } from "src/services/logger";

type DialerEventTypes = "onCallCreated" | "onCallReceived" | "onCallAnswered" | "onCallHangup" | "onServerDisconnect" | "updateStatus" | "onRegistered" | "onUnregistered";

export enum DialerStatus {
  DISCONNECTING,
  DISCONNECTED,
  CONNECTING,
  CONNECTED,
  CALLING,
  INVITED,
  ON_CALL,
  HANGUPING,
  IDLE,
}

type FullApiMediaOptions = {
  remoteAudioEl?: HTMLAudioElement;
  /** constraints default pro getUserMedia (você pode sobrescrever no callTo com deviceId) */
  defaultAudioConstraints?: MediaTrackConstraints;
};

const dialerDomain = import.meta.env.VITE_DIALER_DOMAIN;

// Funções para FULL API do Sip
function makeRemoteStreamFromSession(session: Session): MediaStream | null {
  const sdh: any = (session as any).sessionDescriptionHandler;
  const pc: RTCPeerConnection | undefined = sdh?.peerConnection;
  if (!pc) return null;

  const remoteStream = new MediaStream();
  pc.getReceivers().forEach((r) => {
    if (r.track) remoteStream.addTrack(r.track);
  });
  return remoteStream;
}

function setAudioElementStream(el: HTMLAudioElement, stream: MediaStream) {
  el.srcObject = stream;
  el.autoplay = true;
}

async function sendDtmfBestEffort(session: Session, tone: string) {
  playDTMFTone(tone);

  // 1) tenta RTP (RFC4733) via SDH (quando disponível) :contentReference[oaicite:4]{index=4}
  const sdh: any = (session as any).sessionDescriptionHandler;
  if (sdh?.sendDtmf) {
    const ok = sdh.sendDtmf(tone, { duration: 100, interToneGap: 70 });
    if (ok) return;
  }

  // 2) fallback SIP INFO (guia oficial) :contentReference[oaicite:5]{index=5}
  // SIP.js expõe session.info()
  const body = `Signal=${tone}\r\n` + `Duration=100\r\n`;

  // @ts-ignore (tipagem pode variar por versão)
  await (session as any).info({
    contentType: "application/dtmf-relay",
    contentDisposition: "render",
    content: body,
  });
}

export async function createDialerInstance(
  mediaOptions: FullApiMediaOptions | null,
  instanceName: string,
  options: {
    aderenteSlug: string;
    currentInput?: string;
  },
) {
  // Inner Variables
  let userAgent: UserAgent | null = null;
  let registerer: Registerer | null = null;
  let currentSession: Session | null = null;

  const hasActiveSession = () => currentSession !== null && currentSession.state !== SessionState.Terminated;

  // Tratativa para pam do peerConection
  let inviteInFlight: Promise<any> | null = null;
  let inviteAttemptId = 0;
  let canceledAttemptId: number | null = null;
  let lastInviter: Inviter | null = null;

  let isRegistering = false;
  let reconnectAttempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let isReconnecting = false;
  let callEstablishedAt: number | null = null;

  let ramal: { ramal: string; password: string } | null = null;

  // Inner state
  const state: {
    currentCall: DialerPSTN | null;
    currentInput: string;
    call: Array<DialerPSTN>;
    isInputMuted: boolean;
    isOutputMuted: boolean;
  } = {
    currentCall: null,
    currentInput: options.currentInput || "",
    call: [],
    isInputMuted: false,
    isOutputMuted: false,
  };

  // Status
  let dialerStatus = DialerStatus.DISCONNECTED;

  // Some binds
  const binds: Array<{
    ref: number;
    eventName: string;
    callback: Function;
  }> = [];

  // Private Methods
  const onEvent = async (eventName: DialerEventTypes, args: { ramal?: string; instanceName: string; args?: any }, callback?: Function) => {
    if (callback) callback(args);

    const bindHandlers = binds.filter((b) => b.eventName === eventName);

    if (bindHandlers) await Promise.all(bindHandlers.map((b) => b.callback.call(null, args)));
  };

  const updateStatus = (status: DialerStatus, optionalArgs?: any) => {
    dialerStatus = status;
    logInfo("Dialer: atualização de status", { instanceName, ramal: ramal?.ramal ?? null, status: DialerStatus[status] });
    onEvent("updateStatus", { instanceName, args: { status, ...optionalArgs } });
  };

  // Dealloc ramal
  async function deallocRamal() {
    const dealloc = ramal;

    if (!dealloc) {
      logWarn("Dialer: ramal não informado para desassociação", { instanceName });
      return;
    }

    const { success, message } = await doLeaveRamalRequest(dealloc.ramal);

    if (!success) {
      logWarn("Dialer: falha ao desassociar ramal", { instanceName, ramal: dealloc.ramal, message });
      return;
    }

    logInfo("Dialer: ramal desassociado", { instanceName, ramal: dealloc.ramal });

    ramal = null;

    return true;
  }

  // Status detectors
  const onRegistered = () => {
    isRegistering = false;

    if (hasActiveSession()) {
      logInfo("Dialer: registro SIP restaurado durante chamada ativa", {
        instanceName,
        ramal: ramal?.ramal ?? null,
        sessionState: currentSession!.state,
      });
      return;
    }

    updateStatus(DialerStatus.CONNECTED);
  };
  const onUnregistered = () => {
    isRegistering = false;

    if (hasActiveSession()) {
      logWarn("Dialer: registro SIP perdido durante chamada ativa", {
        instanceName,
        ramal: ramal?.ramal ?? null,
        sessionState: currentSession!.state,
      });

      scheduleReconnect("registerer_unregistered_during_call");
      return;
    }

    updateStatus(DialerStatus.DISCONNECTED);
  };
  const onCallCreated = () => updateStatus(DialerStatus.CALLING);
  const onCallAnswered = () => {
    if (state.currentCall) {
      state.currentCall.startedAt = moment().utcOffset(-3).format("YYYY-MM-DDTHH:mm:ss");
    }

    return updateStatus(DialerStatus.ON_CALL);
  };
  const onCallHangup = () => {
    state.isInputMuted = false;
    state.isOutputMuted = false;

    updateStatus(DialerStatus.IDLE);
  };

  const onInviteSent = () => {
    updateStatus(DialerStatus.INVITED);
  };

  // Mapa de eventos
  function wireSession(session: Invitation | Inviter, kind: "outgoing" | "incoming") {
    currentSession = session;

    const callId = state.currentCall?.internalId;

    logEvent("dialer_session_wired", { callId: callId ?? null, kind, instanceName, ramal: ramal?.ramal ?? null });

    session.stateChange.addListener((newState) => {
      logInfo("Dialer: mudança de estado da sessão", { callId: callId ?? null, kind, sessionState: newState, instanceName, ramal: ramal?.ramal ?? null });

      if (newState === SessionState.Establishing) {
        // Outgoing: INVITE enviado / Ringing
        // Incoming: tocando local até accept/reject
        onInviteSent();
      }

      if (newState === SessionState.Established || newState === SessionState.Establishing) {
        const remoteStream = makeRemoteStreamFromSession(session);
        const remoteAudioEl = mediaOptions?.remoteAudioEl || (document.getElementById("remote-dialer-audio") as HTMLAudioElement);

        if (remoteStream && remoteAudioEl) {
          setAudioElementStream(remoteAudioEl, remoteStream);
        }

        if (newState === SessionState.Established) {
          callEstablishedAt = Date.now();

          logEvent("dialer_call_answered", { callId: callId ?? null, instanceName, ramal: ramal?.ramal ?? null });

          onEvent(
            "onCallAnswered",
            {
              ramal: ramal?.ramal,
              instanceName,
              args: { mediaStream: remoteStream, currentCall: state.currentCall, ramal: ramal?.ramal },
            },
            onCallAnswered,
          );
        }
      }

      if (newState === SessionState.Terminated) {
        const callDurationS = callEstablishedAt ? Math.round((Date.now() - callEstablishedAt) / 1000) : null;

        logEvent("dialer_call_ended", { callId: callId ?? null, kind, instanceName, ramal: ramal?.ramal ?? null, callDurationS: callDurationS ?? null });

        if (callDurationS !== null) {
          logMeasurement("dialer_call_duration_s", { duration: callDurationS }, { callId: callId ?? undefined, instanceName, ramal: ramal?.ramal });
        }

        callEstablishedAt = null;

        onEvent("onCallHangup", { ramal: ramal?.ramal, instanceName, args: { currentCall: state.call.pop() } }, onCallHangup);
        currentSession = null;
        state.currentCall = null;
      }
    });

    if (kind === "incoming") {
      onEvent("onCallReceived", { ramal: ramal?.ramal, instanceName, args: { session, ramal: ramal?.ramal } });
    } else {
      onEvent("onCallCreated", { ramal: ramal?.ramal, instanceName, args: { currentCall: state.currentCall } }, onCallCreated);
    }
  }

  // Exposed functions
  const on = (eventName: DialerEventTypes, callback: Function) => {
    // Create remove ref
    const ref = new Date().getTime();

    // Push list
    binds.push({ ref, eventName, callback });

    return ref;
  };

  const createSimpleUser = async (aderenteSlug: string): Promise<ReturnData> => {
    if (userAgent || registerer) return { success: true, data: { ramal, userAgent, registerer } };

    // Set Status
    updateStatus(DialerStatus.CONNECTING);

    logInfo("Dialer: criando cliente SIP", { instanceName, aderenteSlug });

    if (!ramal) {
      // Allocate ramal
      const { success, data, message } = await doGetRamalRequest(aderenteSlug || "icom-qa");

      // Deny if error
      if (!success) {
        updateStatus(DialerStatus.DISCONNECTED);
        logWarn("Dialer: falha ao alocar ramal", { instanceName, aderenteSlug, message: message ?? null });
        return { success: false, message };
      }
      // Set ramal
      ramal = { ramal: data.ramal, password: data.password };

      logInfo("Dialer: ramal alocado", { instanceName, ramal: ramal.ramal });
    }

    // Set variables
    const webrtcUrl = import.meta.env.VITE_SIP_WS_URL;
    const uri = UserAgent.makeURI(`sip:${ramal.ramal}@${dialerDomain}`);

    const delegate: UserAgentDelegate = {
      onInvite: (invitation: Invitation) => {
        wireSession(invitation, "incoming");
      },
    };

    const registerStartedAt = Date.now();

    // Define client
    try {
      userAgent = new UserAgent({
        uri,
        transportOptions: { server: webrtcUrl },
        authorizationUsername: ramal.ramal,
        authorizationPassword: ramal.password,
        logBuiltinEnabled: false,
        logLevel: "debug",
        logConnector: createSipLogConnector({ instanceName, ramal: ramal.ramal }),
        delegate,
        contactParams: {},
      });

      await userAgent.start();

      registerer = new Registerer(userAgent, {
        logConfiguration: false, // If true, constructor logs the registerer configuration.
        expires: 300,
        refreshFrequency: 75, // Determines when a re-REGISTER request is sent. The value should be specified as a percentage of the expiration time (between 50 and 99).
      });

      registerer.stateChange.addListener((s) => {
        logInfo("Dialer: mudança de estado do registerer", { instanceName, ramal: ramal?.ramal ?? null, registererState: s });

        if (s === RegistererState.Registered) {
          onEvent("onRegistered", { ramal: ramal?.ramal, instanceName }, onRegistered);
        }
        if (s === RegistererState.Unregistered) {
          onEvent("onUnregistered", { instanceName }, onUnregistered);
        }
      });

      isRegistering = true;
      await registerer.register();

      const registerDurationMs = Date.now() - registerStartedAt;

      logMeasurement("dialer_register_duration_ms", { duration: registerDurationMs }, { instanceName, ramal: ramal.ramal });
      logEvent("dialer_registered", { instanceName, ramal: ramal.ramal, registerDurationMs });

      return { success: true, data: { userAgent, registerer, ramal: ramal.ramal } };
    } catch (ex: any) {
      updateStatus(DialerStatus.DISCONNECTED, { error: ex });

      logError("Dialer: falha ao criar/registrar cliente SIP", ex, {
        instanceName,
        ramal: ramal?.ramal ?? null,
        registerDurationMs: Date.now() - registerStartedAt,
      });

      return {
        success: false,
        message: ex.message,
      };
    }
  };

  // Call methods
  const updateCurrentNumber = (newNumber: string) => {
    return (state.currentInput = newNumber);
  };

  function isTransportConnected(): boolean {
    const transport: any = (userAgent as any)?.transport;
    return !!transport && (transport.isConnected?.() === true || transport.connected === true);
  }

  async function hardResetConnection(reason?: unknown) {
    const reasonText = reason instanceof Error ? reason.message : reason ? String(reason) : null;

    logWarn("Dialer: reset de conexão", { instanceName, ramal: ramal?.ramal ?? null, reason: reasonText });

    try {
      await registerer?.unregister?.().catch(() => {});
    } catch {}

    try {
      await userAgent?.stop?.().catch(() => {});
    } catch {}

    registerer = null;
    userAgent = null;
    currentSession = null;
    lastInviter = null;
    inviteInFlight = null;

    updateStatus(DialerStatus.DISCONNECTED, { reason });
  }

  async function attemptReconnect(reason: string) {
    if (isReconnecting || !hasActiveSession()) return;
    isReconnecting = true;

    try {
      if (!userAgent || !registerer) {
        logWarn("Dialer: reconexão sem cliente SIP disponível", { instanceName, reason });
        return;
      }

      if (!isTransportConnected()) {
        logWarn("Dialer: transport ainda desconectado, aguardando nova tentativa", { instanceName, reason });
        scheduleReconnect(reason);
        return;
      }

      // Registro já em andamento; o próprio fluxo de registro cuidará da recuperação.
      if (isRegistering) return;

      isRegistering = true;
      await registerer.register();

      reconnectAttempts = 0;
      logInfo("Dialer: reconexão concluída", { instanceName, reason });
    } catch (ex) {
      isRegistering = false;
      reconnectAttempts += 1;
      const delay = Math.min(1000 * 2 ** reconnectAttempts, 15000);
      logWarn("Dialer: reconexão falhou, nova tentativa em breve", {
        instanceName,
        reason,
        reconnectAttempts,
        delayMs: delay,
      });
      scheduleReconnect(reason, delay);
    } finally {
      isReconnecting = false;
    }
  }

  function scheduleReconnect(reason: string, delayMs = 5000) {
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => void attemptReconnect(reason), delayMs);
  }

  async function ensureConnected() {
    if (!userAgent || !registerer) {
      logInfo("Dialer: cliente SIP inexistente, criando", { instanceName, ramal: ramal?.ramal ?? null });

      const result = await createSimpleUser(options.aderenteSlug);
      if (!result.success) {
        throw new Error(result.message || "Falha ao criar cliente SIP");
      }
    }

    // se o UA existe, mas o transport não está conectado, recrie tudo
    if (!isTransportConnected()) {
      reconnectAttempts += 1;

      logWarn("Dialer: transport desconectado, recriando conexão", {
        instanceName,
        ramal: ramal?.ramal ?? null,
        reconnectAttempts,
      });
      logMeasurement("dialer_reconnect_attempts", { count: reconnectAttempts }, { instanceName, ramal: ramal?.ramal });

      await hardResetConnection("transport_not_connected");

      const result = await createSimpleUser(options.aderenteSlug);
      if (!result.success) {
        throw new Error(result.message || "Falha ao recriar cliente SIP");
      }
    }

    // aqui o UA foi recém-criado ou já estava conectado
    if (!isRegistering) {
      isRegistering = true;
      await registerer!.register();
    }
  }

  const callTo = async (number: string, deviceId?: string) => {
    try {
      await ensureConnected();
    } catch (error) {
      await hardResetConnection(error);
      logError("Dialer: sem conexão com o servidor SIP", error, { instanceName, ramal: ramal?.ramal ?? null, number });
      return {
        success: false,
        message: "Sem conexão com o servidor SIP.",
        error,
      };
    }

    if (currentSession && currentSession.state !== SessionState.Terminated) {
      logWarn("Dialer: chamada recusada, já existe chamada em andamento", { instanceName, ramal: ramal?.ramal ?? null, number });
      return { success: false, message: "Já existe uma chamada em andamento." };
    }

    const callStartedAt = Date.now();

    try {
      // Create new call log
      if (state.currentCall) {
        state.currentCall.numberCalled = number;
      } else {
        const currentCall = new DialerPSTN({
          numberCalled: number,
        });

        state.currentCall = currentCall;
        state.call.push(currentCall);
      }

      const callId = state.currentCall.internalId;

      logEvent("dialer_call_started", { callId, number, instanceName, ramal: ramal?.ramal ?? null });

      const myAttempt = ++inviteAttemptId;
      canceledAttemptId = null;

      const targetUri = UserAgent.makeURI(`sip:${number}@${dialerDomain}`) as URI;

      const inviter = new Inviter(userAgent!, targetUri, {
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: deviceId ? (deviceId === "default" ? true : { deviceId: { exact: deviceId } }) : (mediaOptions?.defaultAudioConstraints ?? true),
            video: false,
          },
        },
        earlyMedia: true,
      });

      // Save for later
      lastInviter = inviter;

      // Map events
      wireSession(inviter, "outgoing");

      // IMPORTANT: guarde a promise e trate erro como "cancelamento" quando for o caso
      inviteInFlight = inviter.invite().catch((err) => {
        const wasCanceled = canceledAttemptId === myAttempt || inviter.state === SessionState.Terminated;
        if (wasCanceled) return; // swallow
        throw err;
      });

      await inviteInFlight;
      inviteInFlight = null;

      const callSetupDurationMs = Date.now() - callStartedAt;

      logMeasurement("dialer_call_setup_duration_ms", { duration: callSetupDurationMs }, { callId, instanceName, ramal: ramal?.ramal });
      logInfo("Dialer: chamada iniciada", { callId, number, instanceName, ramal: ramal?.ramal ?? null, callSetupDurationMs });

      return { success: true, data: { ramal: ramal?.ramal, currentCall: state.currentCall } };
    } catch (ex) {
      updateStatus(DialerStatus.DISCONNECTED, { error: ex });

      logError("Dialer: falha ao iniciar chamada", ex, {
        callId: state.currentCall?.internalId ?? null,
        number,
        instanceName,
        ramal: ramal?.ramal ?? null,
        callSetupDurationMs: Date.now() - callStartedAt,
      });

      // Dismiss current call and inactivate
      state.currentCall = null;
      currentSession = null;

      return { success: false, error: ex };
    }
  };

  const callHangup = async () => {
    const callId = state.currentCall?.internalId ?? null;

    // Salva a instancia de possível status indevido
    if (!currentSession) {
      updateStatus(DialerStatus.IDLE);

      logInfo("Dialer: hangup sem sessão ativa", { callId, instanceName, ramal: ramal?.ramal ?? null });
      return;
    }

    updateStatus(DialerStatus.HANGUPING);

    const s = currentSession;

    logInfo("Dialer: solicitando hangup", { callId, instanceName, ramal: ramal?.ramal ?? null, sessionState: s.state });

    if (inviteInFlight && lastInviter && s === lastInviter) {
      canceledAttemptId = inviteAttemptId;

      logInfo("Dialer: cancelando chamada em andamento", { callId, instanceName, ramal: ramal?.ramal ?? null });

      try {
        await lastInviter.cancel();
      } finally {
        // Espera o invite terminar para não estourar "Peer connection closed" como unhandled
        await inviteInFlight.catch(() => {});
        inviteInFlight = null;
      }
      return;
    }

    if (s.state === SessionState.Initial || s.state === SessionState.Establishing) {
      // outgoing -> cancel; incoming -> reject
      if (s instanceof Inviter) {
        logInfo("Dialer: cancelando sessão (Inviter)", { callId, instanceName, ramal: ramal?.ramal ?? null });
        await s.cancel();
      } else if (s instanceof Invitation) {
        logInfo("Dialer: rejeitando sessão (Invitation)", { callId, instanceName, ramal: ramal?.ramal ?? null });
        await s.reject();
      } else {
        // fallback (não deveria cair aqui)
        logWarn("Dialer: tipo de sessão inesperado no hangup", { callId, instanceName, ramal: ramal?.ramal ?? null });
        await s.dispose?.();
      }
      return;
    }

    if (s.state === SessionState.Established) {
      logInfo("Dialer: encerrando chamada estabelecida (BYE)", { callId, instanceName, ramal: ramal?.ramal ?? null });
      await s.bye();
    }
  };

  const sendDTMF = async (key: string) => {
    if (!currentSession) return;
    await sendDtmfBestEffort(currentSession, key);
  };

  const transferCall = (transferRamal: string) => {
    if (!currentSession) return;
    const targetUri = UserAgent.makeURI(`sip:${transferRamal}@${dialerDomain}`);
    if (!targetUri) return;

    return currentSession.refer(targetUri);
  };

  const createCall = (number: string, called?: boolean, externalId?: string) => {
    const newCall = new DialerPSTN({
      numberCalled: number,
      externalId,
    });

    state.call.push(newCall);
    state.currentCall = newCall;
    state.currentInput = newCall.numberCalled!;

    return state.currentCall;
  };

  const toggleInputMute = () => {
    if (!currentSession) return (state.isInputMuted = !state.isInputMuted);

    const sdh: any = (currentSession as any).sessionDescriptionHandler;
    const pc: RTCPeerConnection | undefined = sdh?.peerConnection;
    if (!pc) return (state.isInputMuted = !state.isInputMuted);

    const senders = pc.getSenders().filter((x) => x.track?.kind === "audio");
    const currentlyEnabled = !!senders[0]?.track?.enabled;

    // mute = desabilitar track local
    senders.forEach((s) => {
      if (s.track) s.track.enabled = !currentlyEnabled;
    });

    state.isInputMuted = currentlyEnabled; // se estava enabled, agora mutou
    return state.isInputMuted;
  };

  const toggleOutputMute = () => {
    const stream = currentSession ? makeRemoteStreamFromSession(currentSession) : null;
    if (!stream) return null;

    const track = stream.getAudioTracks()[0];
    if (!track) return null;

    state.isOutputMuted = !state.isOutputMuted;
    track.enabled = !state.isOutputMuted;
    return state.isOutputMuted;
  };

  const disconnectClient = async () => {
    logInfo("Dialer: encerrando discador", { instanceName, ramal: ramal?.ramal ?? null });

    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    try {
      if (ramal) await deallocRamal();

      if (currentSession && currentSession.state !== SessionState.Terminated) {
        await callHangup();
      }
      currentSession = null;

      if (registerer) await registerer.unregister();
      registerer = null;

      if (userAgent) await userAgent.stop();
      userAgent = null;

      logInfo("Dialer: discador encerrado", { instanceName });
    } catch (ex) {
      logError("Dialer: falha ao encerrar discador", ex, { instanceName, ramal: ramal?.ramal ?? null });
    }
  };

  const destroy = async () => {
    clearBinds();
    await disconnectClient();
    return true;
  };

  // Helpers methods
  const clearBinds = (bindsArray?: Array<number>) => {
    if (bindsArray)
      return bindsArray.forEach((id) => {
        const index = binds.findIndex((b) => b.ref === id);
        return !!binds.splice(index, 1).length;
      });

    return binds.splice(0, binds.length);
  };

  return {
    // Call controls
    callTo,
    transferCall,
    callHangup,
    createSimpleUser,
    disconnectSimpleUser: disconnectClient,
    updateCurrentNumber,
    sendDTMF,
    toggleInputMute,
    toggleOutputMute,

    // Ultilities
    on,
    clearBinds,
    destroy,
    createCall,

    // Vars
    instanceName,

    // getters
    getStatus: () => dialerStatus,
    getInput: () => state.currentInput,
    getCurrentCall: () => state.currentCall,
    getRamal: () => ramal?.ramal,
    isInputMuted: () => state.isInputMuted,
    isOutputMuted: () => state.isOutputMuted,

    // extra: pra quem quiser usar fora (debug/UI)
    getSession: () => currentSession,
    getRemoteMediaStream: () => (currentSession ? makeRemoteStreamFromSession(currentSession) : null),
    isOnCall: () => currentSession !== null && currentSession.state === SessionState.Established,
    getSessionState: () => currentSession?.state ?? null,
  };
}

export type DialerInstance = Awaited<ReturnType<typeof createDialerInstance>>;
